import { FC, useState } from "react";
import * as yup from 'yup';
import { Notification } from "../../common/Notification/Notification";
import { defaultNotifState } from "../../constants/currency-constants";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import './CopyCampaignModal.css';
import { Close as CloseIcon } from "@mui/icons-material";
import { Formik,  FormikProps,  getIn } from "formik";
import { Campaign } from "../../types/campaign";
import TooltipText from "../inputComponents/TooltipText";
import { InputComponent } from "../inputComponents/InputComponent";
import { ToolTipPlacement } from "../../types/componentTypes/tooltip";
import { DateRangePickerWrapper } from "../selectComponents/DateRangePicker";
import { API, Auth, graphqlOperation } from "aws-amplify";
import uniqid from "uniqid";
import { createPromotionsTable } from "../../graphql/mutations";
import moment from "moment";

type CopyCampaignModalProps = {
    isOpen: boolean;
    close: () => void;
    copiedCampaign: Campaign;
    campaigns: Campaign[];
    refetchCampaigns: () => void;
};

interface InitialValues extends Campaign {
    promotion_dateRange?: Array<number>,
}

type StepAction = 'cancel' | 'previous' | 'next';

const allowedKeys = [
    'digital_experience',
    'promotion_name',
    'promotion_fullName',
    'promotion_market',
    'promotion_owner',
    'entry_date',
    'promotion_author',
    'promotion_start_utc',
    'promotion_end_utc',
    'promotion_id',
    'promotion_brand'
];

function copyCampaignValidationSchema() {
    return yup.object({
        promotion_name: yup
            .string()
            .max(90,'The maximum length of the campaign name is 90 characters')
            .required('Campaign name cannot be empty'),
        promotion_fullName: yup
            .string()
            .max(250,'The maximum length of the campaign full name is 250 characters')
            .required('Campaign full name cannot be empty'),
    });
}

const CopyCampaignModal: FC<CopyCampaignModalProps> = ({
    isOpen, close, copiedCampaign, campaigns, refetchCampaigns
}) => {
    const [notification, setNotification] = useState(defaultNotifState);
    const [step, setStep] = useState<number>(0);
    const isFirstStep = step === 0;

    const initValues = () => {
        const newStartDate = moment().toDate().getTime();
        const newEndDate = moment().add(30, 'days').toDate().getTime();
        let initialValues = { ...copiedCampaign } as InitialValues;

        if (copiedCampaign?.archived) {
            initialValues.promotion_dateRange = [
                newStartDate, newEndDate
            ]
            initialValues.promotion_start_utc = newStartDate;
            initialValues.promotion_end_utc = newEndDate;
        } else {
            initialValues.promotion_dateRange = [
                copiedCampaign.promotion_start_utc,
                copiedCampaign.promotion_end_utc,
            ]
        }

        return initialValues;
    }

    const closeModal = () => {
        close();
        setStep(0);
    };

    const findDuplicates = (promoName: string, promoFullName: string) => campaigns
        ?.reduce((acc, campaign) => {
            const promoNameMatch = getBaseName(promoName) === getBaseName(campaign?.promotion_name)
            const promoFullNameMatch = getBaseName(promoFullName) === getBaseName(campaign?.promotion_fullName)
            if (promoNameMatch && promoFullNameMatch) acc.push(campaign);
            return acc;
        }, [])

    const getBaseName = (word: string) => {
         if (word && word?.includes('copy')) {
            const wordsAndSeparators = [...word.matchAll(/(\w+|\W+)/g)].map(m => m[0]);
            const copyWordIdx = wordsAndSeparators.findIndex(word => word === 'copy');
            const originalName = wordsAndSeparators.slice(0, copyWordIdx).join('').trim();
            return originalName
        }
        return word;
    }

    const createCopyName = (name: string, duplicates: Campaign[]) => `${getBaseName(name)} copy ${duplicates.length}`;

    const prepareNewCampaignObjCopy = (data: Campaign) => {
        const newCampaignCopy: Partial<Campaign> = {};
        Object.entries(data).forEach(([key, val]) => allowedKeys.includes(key) && (newCampaignCopy[key] = val));

        Auth.currentAuthenticatedUser()
            .then(userData => {
                const userEmail = userData?.attributes?.email;
                newCampaignCopy.promotion_owner = userEmail;
                newCampaignCopy.promotion_author = userEmail;
            })
            .catch(() => console.log('Not signed in when trying to create a campaign copy'));

        newCampaignCopy.promotion_id = (uniqid() + uniqid()).substring(0, 14);
        newCampaignCopy.entry_date = moment().format('YYYY-MM-DD');
        const campaignName = newCampaignCopy.promotion_name;
        const campaignFullName = newCampaignCopy.promotion_fullName;
        const duplicates = findDuplicates(campaignName, campaignFullName);
        if (duplicates?.length !== 0 ) {
            newCampaignCopy.promotion_name = createCopyName(campaignName, duplicates);
            newCampaignCopy.promotion_fullName = createCopyName(campaignFullName, duplicates);
        };
        return newCampaignCopy;
    };

    const handleStepAction = (
        action: StepAction,
        formik?: FormikProps<any>
    ) => {
        const actions: Record<StepAction, () => void> = {
            cancel: () => {
                formik?.resetForm();
                closeModal();
            },
            previous: () => setStep(0),
            next: () => setStep(1),
        };
        actions[action]?.();
    };

    return (
        <>
            <Notification notificationState={notification} setNotificationState={setNotification} />
            <Modal open={isOpen} onClose={closeModal}>
                <Box className='copy-campaign-modal'>
                    <IconButton onClick={() => closeModal()}
                        sx={{ position: 'absolute', top: 4, right: 4 }}>
                        <CloseIcon />
                    </IconButton>
                    <Formik onSubmit={ async (values, { resetForm }) => {
                        try {
                            const newCampaignCopy = prepareNewCampaignObjCopy(values);
                            await API.graphql(graphqlOperation(createPromotionsTable, {input: newCampaignCopy}));
                            closeModal();
                            resetForm();
                            refetchCampaigns();
                            setNotification({
                                open: true,
                                title: "Campaign is copied successfully.",
                                content: '',
                                level: "success"
                            });
                        } catch (error) {
                            console.error("Save failed with: ", error);
                            setNotification({
                                open: true,
                                title: 'Error',
                                content: `Copy failed! Please check console for detailed error.`,
                                level: "error"
                            })
                        }
                    }}
                        initialValues={{ ...initValues() }}
                        validationSchema={copyCampaignValidationSchema()}
                    >
                        {formik => (
                            <form className='copy-campaign-form' onSubmit={formik.handleSubmit}>
                                {isFirstStep ?
                                    <>
                                        <Box>
                                            <TooltipText
                                            text="Campaign Name"
                                            textVariant="h6"
                                            tooltip="Choose a new campaign name and fullname. In the inputs you will see the default values, if you leave them and your old campaign still exists, names will be automatically assigned with 'copy' in each name and a number at the end. Example: Campaign copy 1 (if you have more copies of the same campaign: Campaign copy 2 and so on)."
                                            placement={ToolTipPlacement.RightEnd}
                                        />
                                            <Typography variant="body2" align='center'>
                                                Choose new names for your campaign copy
                                            </Typography>
                                        </Box>
                                        <InputComponent
                                            formik={formik}
                                            inputValue={getIn(formik.values, 'promotion_name')}
                                            label="Campaign Name"
                                            formikLabel='promotion_name'
                                            titleVariant="body1"
                                            dataTestId="promotion_name"
                                        />
                                        <InputComponent
                                            formik={formik}
                                            inputValue={getIn(formik.values, 'promotion_fullName')}
                                            label="Campaign Fullname"
                                            formikLabel='promotion_fullName'
                                            titleVariant="body1"
                                            dataTestId="promotion_fullName"
                                        />
                                    </>
                                        :
                                    <>
                                        <Box>
                                            <TooltipText
                                            text="Allocate Dates"
                                            textVariant="h6"
                                            tooltip="The start and end date are defaulted from the campaign you are copying from. If a campaign is archived you will automatically get today's date as a start date + 30 days as an end date. "
                                            placement={ToolTipPlacement.RightEnd}
                                        />
                                            <Typography variant="body2" align='center'>
                                                Select a start and end date for the overall duration of the campaign
                                            </Typography>
                                        </Box>
                                        <DateRangePickerWrapper
                                            title=""
                                            description=""
                                            datesClass="copy-campaign-dates"
                                            formikStartDate={'promotion_start_utc'}
                                            formikEndDate={'promotion_end_utc'}
                                            formikDateRange={'promotion_dateRange'}
                                        />
                                    </>
                                }
                                <Box display='flex' gap='1.1rem'>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => handleStepAction(isFirstStep ? 'cancel' : 'previous', formik)}
                                        type="button"
                                    >
                                        {isFirstStep ? 'Cancel' : 'Previous'}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={
                                            isFirstStep ? async (e) => {
                                                e.preventDefault();
                                                const errors = await formik.validateForm();
                                                const nameErr = getIn(errors, 'promotion_name');
                                                const fullNameErr = getIn(errors, 'promotion_fullName');

                                                if (!nameErr && !fullNameErr) {
                                                    handleStepAction('next');
                                                } else {
                                                    formik.setTouched({
                                                        promotion_name: true,
                                                        promotion_fullName: true
                                                    })
                                                }
                                            } : undefined
                                        }
                                        type={isFirstStep ? 'button' : 'submit'}
                                    >
                                        {isFirstStep ? 'Next' : 'Save Copy'}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </>
    );
}

export default CopyCampaignModal;
